import { useRef, useState, useLayoutEffect, useId } from 'react';
import type { CalcResult, UsedStock } from '@app/linecut-core';
import { Collapsible } from '../../components/Collapsible';
import type { ReportSettings } from './useReportSettings';

export interface MaterialResult {
  material: string;
  result: CalcResult;
}

interface Props {
  results: MaterialResult[];
  blade: number;
  trim: number;
  settings: ReportSettings;
  onToggle: (key: keyof ReportSettings) => void;
}

const REPORT_TOGGLES: Array<{ key: keyof ReportSettings; label: string }> = [
  { key: 'fromZero',      label: 'Показывать размеры от нуля' },
  { key: 'showScheme',    label: 'Схема раскроя' },
  { key: 'showPieceTags', label: 'Список деталей под схемой' },
  { key: 'showTextScheme',label: 'Схема раскроя в текстовом формате' },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const PIECE_COLORS = [
  'var(--color-piece-1)',
  'var(--color-piece-2)',
  'var(--color-piece-3)',
  'var(--color-piece-4)',
  'var(--color-piece-5)',
];

const SVG_W = 1000;
const BAR_H = 20;
const KERF_COLOR = 'var(--color-kerf)';
const BORDER = '1px solid var(--color-track)';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtN(n: number): string {
  return String(Math.round(n));
}

function fmt(n: number): string {
  return `${Math.round(n)}мм`;
}

function groupBy<T>(
  items: T[],
  key: (item: T) => string,
): Array<{ count: number; item: T }> {
  const groups = new Map<string, { count: number; item: T }>();
  for (const item of items) {
    const k = key(item);
    const g = groups.get(k);
    if (g) g.count++;
    else groups.set(k, { count: 1, item });
  }
  return Array.from(groups.values());
}

function declPiece(n: number): string {
  const mod10 = n % 10,
    mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 19) return 'деталей';
  if (mod10 === 1) return 'деталь';
  if (mod10 >= 2 && mod10 <= 4) return 'детали';
  return 'деталей';
}

// ─── Toggle switch ────────────────────────────────────────────────────────────

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        cursor: 'pointer',
        userSelect: 'none',
        borderBottom: '1px solid var(--color-track)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--color-black)' }}>{label}</span>
      <div
        onClick={onChange}
        style={{
          width: 36,
          height: 20,
          borderRadius: 10,
          padding: 2,
          background: checked ? 'var(--color-red)' : 'var(--color-track)',
          transition: 'background 150ms',
          cursor: 'pointer',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            transition: 'transform 150ms',
            transform: checked ? 'translateX(16px)' : 'translateX(0)',
          }}
        />
      </div>
    </label>
  );
}

// ─── StockBar ─────────────────────────────────────────────────────────────────

function StockBar({
  stock,
  fromZero,
  count,
  showPieceTags,
}: {
  stock: UsedStock;
  fromZero: boolean;
  count: number;
  showPieceTags: boolean;
}) {
  const clipId = useId();
  const n = stock.pieces.length;
  const trimPerSide = stock.trimWaste / 2;

  function f(v: number): number {
    return v / stock.length;
  }

  const numCuts = stock.remainder > 0 ? n : Math.max(0, n - 1);
  const bladePerCut =
    numCuts > 0 && stock.bladeWaste > 0 ? stock.bladeWaste / numCuts : 0;

  // Hide blade label if blade width < 1% of stock length
  const showBladeLabel = bladePerCut / stock.length >= 0.01;

  type SegInfo = { x: number; w: number; color: string; title: string; isRem?: boolean };
  type LabelInfo = { cx: number; text: string; isRem?: boolean };
  type BladeInfo = { cx: number; text: string };
  type CutLine = { left: number; right: number; center: number };

  // Group consecutive same-length same-name pieces
  const pieceGroups: Array<{ length: number; partName: string; count: number }> = [];
  for (const piece of stock.pieces) {
    const last = pieceGroups[pieceGroups.length - 1];
    const canMerge = last && last.length === piece.length && last.partName === piece.partName;
    if (canMerge) last!.count++;
    else pieceGroups.push({ length: piece.length, partName: piece.partName, count: 1 });
  }

  const flatPieces: Array<{ length: number; partName: string; groupIdx: number }> = [];
  for (const [gi, group] of pieceGroups.entries()) {
    for (let k = 0; k < group.count; k++) {
      flatPieces.push({ length: group.length, partName: group.partName, groupIdx: gi });
    }
  }

  // --- Merged mode ---
  const mergedSegs: SegInfo[] = [];
  const mergedLbls: LabelInfo[] = [];
  const mergedBlade: BladeInfo[] = [];
  const mergedCuts: CutLine[] = [];

  {
    let cur = 0;
    if (trimPerSide > 0) {
      mergedSegs.push({ x: f(cur), w: f(trimPerSide), color: 'var(--color-track)', title: `Торцовка: ${fmtN(trimPerSide)} мм` });
      cur += trimPerSide;
    }
    pieceGroups.forEach((group, gi) => {
      const groupStart = cur;
      const isLastGroup = gi === pieceGroups.length - 1;
      const hasCutAfter = !isLastGroup || stock.remainder > 0;
      const groupSpan = group.count * group.length + (group.count - 1) * bladePerCut;
      mergedSegs.push({
        x: f(groupStart), w: f(groupSpan),
        color: PIECE_COLORS[gi % PIECE_COLORS.length],
        title: group.count > 1
          ? `${group.count}× ${group.partName}: ${fmtN(group.length)} мм`
          : `${group.partName}: ${fmtN(group.length)} мм`,
      });
      mergedLbls.push({
        cx: f(groupStart + groupSpan / 2),
        text: group.count > 1 ? `${group.count}×${fmtN(group.length)}` : fmtN(group.length),
      });
      cur += groupSpan;
      if (hasCutAfter) {
        const cutLeft = cur;
        const cutCenter = cur + bladePerCut / 2;
        const cutRight = cur + bladePerCut;
        mergedCuts.push({ left: f(cutLeft), right: f(cutRight), center: f(cutCenter) });
        if (bladePerCut > 0) mergedBlade.push({ cx: f(cutCenter), text: fmtN(bladePerCut) });
        cur = cutRight;
      }
    });
    if (trimPerSide > 0) {
      mergedSegs.push({ x: f(cur), w: f(trimPerSide), color: 'var(--color-track)', title: `Торцовка: ${fmtN(trimPerSide)} мм` });
      cur += trimPerSide;
    }
    if (stock.remainder > 0) {
      const remStart = cur;
      mergedSegs.push({
        x: f(remStart), w: f(stock.remainder),
        color: stock.isBusinessRemainder ? 'var(--color-in-stock-subtle)' : 'var(--color-bg)',
        title: stock.isBusinessRemainder ? `Деловой остаток: ${fmtN(stock.remainder)} мм` : `Отход: ${fmtN(stock.remainder)} мм`,
        isRem: true,
      });
      mergedLbls.push({ cx: f(remStart + stock.remainder / 2), text: fmtN(stock.remainder), isRem: true });
    }
  }

  // --- From-zero mode ---
  const indivSegs: SegInfo[] = [];
  const indivLbls: LabelInfo[] = [];
  const indivCuts: CutLine[] = [];

  {
    let cur = 0;
    let cumDist = 0;
    if (trimPerSide > 0) {
      indivSegs.push({ x: f(cur), w: f(trimPerSide), color: 'var(--color-track)', title: `Торцовка: ${fmtN(trimPerSide)} мм` });
      cur += trimPerSide;
      cumDist += trimPerSide;
    }
    flatPieces.forEach((piece, pi) => {
      const isLast = pi === flatPieces.length - 1;
      const hasCutAfter = !isLast || stock.remainder > 0;
      indivSegs.push({ x: f(cur), w: f(piece.length), color: PIECE_COLORS[piece.groupIdx % PIECE_COLORS.length], title: `${piece.partName}: ${fmtN(piece.length)} мм` });
      cur += piece.length;
      cumDist += piece.length;
      indivLbls.push({ cx: f(cur), text: fmtN(cumDist) });
      if (hasCutAfter) {
        const cutLeft = cur;
        const cutCenter = cur + bladePerCut / 2;
        const cutRight = cur + bladePerCut;
        indivCuts.push({ left: f(cutLeft), right: f(cutRight), center: f(cutCenter) });
        cur = cutRight;
        cumDist += bladePerCut;
      }
    });
    if (trimPerSide > 0) {
      indivSegs.push({ x: f(cur), w: f(trimPerSide), color: 'var(--color-track)', title: `Торцовка: ${fmtN(trimPerSide)} мм` });
      cur += trimPerSide;
    }
    if (stock.remainder > 0) {
      indivSegs.push({
        x: f(cur), w: f(stock.remainder),
        color: stock.isBusinessRemainder ? 'var(--color-in-stock-subtle)' : 'var(--color-bg)',
        title: stock.isBusinessRemainder ? `Деловой остаток: ${fmtN(stock.remainder)} мм` : `Отход: ${fmtN(stock.remainder)} мм`,
        isRem: true,
      });
    }
  }

  const barRef = useRef<SVGSVGElement>(null);
  const [barWidth, setBarWidth] = useState(600);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setBarWidth(e.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const naturalRemFrac = f(stock.remainder);
  const isRemClamped = stock.remainder > 0 && barWidth > 0 && naturalRemFrac * barWidth > 35;
  const displayRemFrac = isRemClamped ? 35 / barWidth : naturalRemFrac;
  const leftScale =
    isRemClamped && naturalRemFrac < 1 ? (1 - displayRemFrac) / (1 - naturalRemFrac) : 1;

  function lx(frac: number): number {
    return frac * leftScale * SVG_W;
  }

  const compactThreshold = Math.max(30, 0.05 * barWidth);
  const isCompact =
    barWidth > 0 &&
    (() => {
      if (fromZero) {
        return flatPieces.some((p) => f(p.length) * leftScale * barWidth < compactThreshold);
      }
      return pieceGroups.some((g) => {
        const span = g.count * g.length + (g.count - 1) * bladePerCut;
        return f(span) * leftScale * barWidth < compactThreshold;
      });
    })();

  function segSvgX(seg: SegInfo): number {
    return seg.isRem ? (1 - displayRemFrac) * SVG_W : lx(seg.x);
  }
  function segSvgW(seg: SegInfo): number {
    return seg.isRem ? displayRemFrac * SVG_W : lx(seg.w);
  }

  function renderSeg(seg: SegInfo, i: number) {
    const sx = segSvgX(seg);
    const sw = segSvgW(seg);

    if (seg.isRem && isRemClamped && barWidth > 0) {
      const amp = (4 * SVG_W) / barWidth;
      const period = 8;
      let d = `M ${sx},${BAR_H} L ${sx},0 L ${sx + sw},0`;
      let y = 0;
      let sign = 1;
      while (y < BAR_H) {
        const ny = Math.min(y + period, BAR_H);
        d += ` Q ${sx + sw + sign * amp},${(y + ny) / 2} ${sx + sw},${ny}`;
        y = ny;
        sign = -sign;
      }
      d += ' Z';
      return <path key={i} d={d} fill={seg.color}><title>{seg.title}</title></path>;
    }

    return (
      <rect key={i} x={sx} y={0} width={sw} height={BAR_H} fill={seg.color}>
        <title>{seg.title}</title>
      </rect>
    );
  }

  function renderCut(cut: CutLine, i: number) {
    const xl = lx(cut.left);
    const xr = lx(cut.right);
    const xc = lx(cut.center);
    const hasBlade = cut.left !== cut.right;
    const edgeX = hasBlade ? xl : xc;
    return (
      <g key={i}>
        {hasBlade && (
          <rect x={xl} y={0} width={xr - xl} height={BAR_H} fill={KERF_COLOR} />
        )}
        <line x1={edgeX} y1={0} x2={edgeX} y2={BAR_H}
          stroke="var(--color-black)" strokeWidth={1} strokeDasharray="4,3"
          vectorEffect="non-scaling-stroke" />
        {hasBlade && (
          <>
            <line x1={xr} y1={0} x2={xr} y2={BAR_H}
              stroke="var(--color-black)" strokeWidth={1} strokeDasharray="4,3"
              vectorEffect="non-scaling-stroke" />
            <line x1={xc} y1={0} x2={xc} y2={BAR_H}
              stroke="var(--color-black)" strokeWidth={1} strokeDasharray="10,4,2,4"
              vectorEffect="non-scaling-stroke" />
          </>
        )}
      </g>
    );
  }

  function lblPct(lbl: LabelInfo): number {
    return lbl.isRem ? (1 - displayRemFrac / 2) * 100 : (lx(lbl.cx) / SVG_W) * 100;
  }

  return (
    <div style={{ background: 'var(--color-white)', border: BORDER, borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {count > 1 && (
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-gray)', letterSpacing: '0.03em', textTransform: 'uppercase', flexShrink: 0 }}>
            {count}×
          </span>
        )}
        <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--color-black)' }}>
          {fmt(stock.length)}
        </span>
        {stock.material && (
          <span style={{ fontSize: 12, color: 'var(--color-white)', background: 'var(--color-gray)', borderRadius: 4, padding: '1px 7px' }}>
            {stock.material}
          </span>
        )}
        <span style={{ fontSize: 12, color: 'var(--color-gray)', marginLeft: 'auto' }}>
          {stock.pieces.length} {declPiece(stock.pieces.length)}
          {stock.remainder > 0 && (
            <span style={{ color: 'var(--color-gray)' }}>
              &nbsp;· отход {fmt(stock.remainder)}
            </span>
          )}
        </span>
      </div>

      {/* SVG bar */}
      <div style={{ position: 'relative', paddingBottom: isCompact ? 50 : 20 }}>
        <svg ref={barRef}
          style={{ display: 'block', width: '100%', height: BAR_H }}
          viewBox={`0 0 ${SVG_W} ${BAR_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={0} y={0} width={SVG_W} height={BAR_H} />
            </clipPath>
          </defs>
          <rect x={0} y={0} width={SVG_W} height={BAR_H} fill="var(--color-bg)" />
          <g clipPath={`url(#${clipId})`}>
            <g visibility={fromZero ? 'hidden' : 'visible'}>{mergedSegs.map(renderSeg)}</g>
            <g visibility={fromZero ? 'visible' : 'hidden'}>{indivSegs.map(renderSeg)}</g>
          </g>
          {isRemClamped ? (
            <>
              <line x1={0} y1={0} x2={SVG_W} y2={0} stroke="var(--color-track)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              <line x1={0} y1={BAR_H} x2={SVG_W} y2={BAR_H} stroke="var(--color-track)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              <line x1={0} y1={0} x2={0} y2={BAR_H} stroke="var(--color-track)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            </>
          ) : (
            <rect x={0} y={0} width={SVG_W} height={BAR_H} fill="none" stroke="var(--color-track)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          )}
          <g visibility={fromZero ? 'hidden' : 'visible'}>{mergedCuts.map(renderCut)}</g>
          <g visibility={fromZero ? 'visible' : 'hidden'}>{indivCuts.map(renderCut)}</g>
        </svg>

        {/* Merged labels */}
        {!fromZero && mergedLbls.map((lbl, i) => (
          <div key={i} style={{
            position: 'absolute', top: 26, left: `${lblPct(lbl)}%`,
            display: 'flex', flexDirection: isCompact ? 'column' : 'row',
            alignItems: 'center', gap: isCompact ? 1 : 2,
            fontSize: isCompact ? 9 : 10, color: 'var(--color-black)',
            whiteSpace: 'nowrap', lineHeight: 1, pointerEvents: 'none',
            transform: isCompact ? 'translateX(-50%)' : undefined,
          }}>
            <span style={{ color: 'var(--color-gray)', lineHeight: 1 }}>↑</span>
            <span style={isCompact ? { writingMode: 'vertical-rl', fontSize: 9, lineHeight: 1 } : {}}>{lbl.text}</span>
          </div>
        ))}

        {/* Merged blade labels — hidden if blade < 1% of stock */}
        {!fromZero && showBladeLabel && mergedBlade.map((bl, i) => (
          <div key={i} style={{
            position: 'absolute', bottom: 0,
            left: `${(lx(bl.cx) / SVG_W) * 100}%`,
            transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            pointerEvents: 'none', lineHeight: 1.3,
          }}>
            <span style={{ fontSize: 9, color: 'var(--color-gray)', whiteSpace: 'nowrap' }}>{bl.text}</span>
            <span style={{ fontSize: 10, color: 'var(--color-gray)', lineHeight: 1 }}>↔</span>
          </div>
        ))}

        {/* From-zero labels */}
        {fromZero && indivLbls.map((lbl, i) => (
          <div key={i} style={{
            position: 'absolute', top: 26,
            left: `${(lx(lbl.cx) / SVG_W) * 100}%`,
            transform: isCompact ? 'translateX(-50%)' : 'translateX(-10%)',
            display: 'flex', flexDirection: isCompact ? 'column' : 'row',
            alignItems: 'center', gap: isCompact ? 1 : 2,
            fontSize: isCompact ? 9 : 10, color: 'var(--color-black)',
            whiteSpace: 'nowrap', lineHeight: 1, pointerEvents: 'none',
          }}>
            <span style={{ color: 'var(--color-gray)', lineHeight: 1 }}>↑</span>
            <span style={isCompact ? { writingMode: 'vertical-rl', fontSize: 9, lineHeight: 1 } : {}}>{lbl.text}</span>
          </div>
        ))}
      </div>

      {/* Piece tags */}
      {showPieceTags && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {pieceGroups.map((group, gi) => (
          <span key={gi} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '3px 8px', background: 'var(--color-bg)',
            border: BORDER, borderRadius: 4, fontSize: 12, color: 'var(--color-black)',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: PIECE_COLORS[gi % PIECE_COLORS.length] }} />
            {group.count > 1 ? `${group.count}× ` : ''}
            {group.partName !== String(group.length)
              ? `${group.partName} · ${fmt(group.length)}`
              : fmt(group.length)}
          </span>
        ))}
      </div>}
    </div>
  );
}

// ─── TextScheme ───────────────────────────────────────────────────────────────

function TextScheme({
  stocks,
  fromZero,
  blade,
  trim,
}: {
  stocks: UsedStock[];
  fromZero: boolean;
  blade: number;
  trim: number;
}) {
  const groups = groupBy(stocks, (s) => `${s.length}|${s.material}|${s.pieces.map((p) => p.length).join(',')}`);

  const primaryLines: string[] = [];
  const fromZeroLines: string[] = [];

  for (const { count, item: rep } of groups) {
    const n = rep.pieces.length;
    const numCuts = rep.remainder > 0 ? n : Math.max(0, n - 1);
    const bladePerCut = numCuts > 0 && blade > 0 ? blade : 0;

    const pieceParts = rep.pieces.map((piece, i) => {
      const seg = `[ ${fmtN(piece.length)}мм ]`;
      return i === 0 ? seg : `${blade > 0 ? `{${fmtN(blade)}}` : ''}${seg}`;
    });
    primaryLines.push(`${count}x ${pieceParts.join('')}`);

    const positions: number[] = [];
    let cursor = trim;
    rep.pieces.forEach((piece, i) => {
      cursor += piece.length;
      const hasCutAfter = i < n - 1 || rep.remainder > 0;
      if (hasCutAfter) {
        positions.push(cursor + bladePerCut / 2);
        cursor += bladePerCut;
      }
    });
    if (positions.length > 0) {
      fromZeroLines.push(`${count}x | ${positions.map((p) => `${fmtN(p)}мм`).join(' | ')} |`);
    }
  }

  const text =
    fromZero && fromZeroLines.length > 0
      ? `${primaryLines.join('\n')}\n\nРазмеры от нуля:\n${fromZeroLines.join('\n')}`
      : primaryLines.join('\n');

  return (
    <div style={{ background: 'var(--color-white)', border: BORDER, borderRadius: 8, padding: '16px 20px' }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--color-black)', fontFamily: 'Montserrat, sans-serif' }}>
        Схема раскроя (текстовый формат)
      </h4>
      <pre style={{ margin: 0, fontFamily: '"Courier New", Courier, monospace', fontSize: 12, lineHeight: 1.7, color: 'var(--color-black)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {text}
      </pre>
    </div>
  );
}

// ─── MaterialResultCard ───────────────────────────────────────────────────────

function MaterialResultCard({
  material,
  result,
  settings,
  blade,
  trim,
}: {
  material: string;
  result: CalcResult;
  settings: ReportSettings;
  blade: number;
  trim: number;
}) {
  const { totalBars, totalPieces, efficiency, unplacedCount, unplacedParts, usedStocks, totalWasteLength } = result;
  const effColor = efficiency >= 90 ? 'var(--color-in-stock)' : efficiency >= 75 ? '#d97706' : 'var(--color-red)';

  const stockGroups = groupBy(usedStocks, (s) => `${s.length}|${s.pieces.map((p) => p.length).join(',')}`);

  const badge = unplacedCount > 0 ? (
    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-red)', background: 'var(--color-red-subtle)', padding: '2px 8px', borderRadius: 4 }}>
      ⚠ {unplacedCount} не влезло
    </span>
  ) : null;

  return (
    <Collapsible title={<span style={{ fontFamily: 'Montserrat, sans-serif' }}>{material}</span>} defaultOpen={true} headerRight={badge ?? undefined}>
      <div className="space-y-3 p-4">
        {/* Summary stats */}
        <div style={{ display: 'flex', background: 'var(--color-white)', border: BORDER, borderRadius: 8, overflow: 'hidden' }}>
          {[
            { value: totalBars,                          label: 'заготовок',          color: 'var(--color-black)' },
            { value: totalPieces,                        label: 'деталей размещено',  color: 'var(--color-black)' },
            { value: `${efficiency}%`,                   label: 'эффективность',      color: effColor },
            { value: `${Math.round(totalWasteLength)} мм`, label: 'общий отход',      color: 'var(--color-black)' },
          ].map(({ value, label, color }, i, arr) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 12px', gap: 4, borderRight: i < arr.length - 1 ? BORDER : 'none' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{value}</span>
              <span style={{ fontSize: 11, color: 'var(--color-gray)', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Unplaced parts */}
        {unplacedCount > 0 && (
          <div style={{ background: 'var(--color-red-subtle)', border: '1px solid rgba(206,43,39,0.2)', borderRadius: 8, padding: 16 }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--color-red)' }}>
              Не удалось разместить ({unplacedCount}):
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unplacedParts.map((p, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', background: 'var(--color-white)', border: '1px solid rgba(206,43,39,0.3)', borderRadius: 4, fontSize: 12, color: 'var(--color-red)' }}>
                  {p.name !== String(p.length) ? `${p.name} · ` : ''}{fmt(p.length)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stock bars */}
        {settings.showScheme && (
          <div className="space-y-2">
            {stockGroups.map(({ count, item: stock }) => (
              <StockBar key={`${stock.length}|${stock.pieces.map((p) => p.length).join(',')}`} stock={stock} fromZero={settings.fromZero} count={count} showPieceTags={settings.showPieceTags} />
            ))}
          </div>
        )}

        {/* Text scheme */}
        {settings.showTextScheme && (
          <TextScheme stocks={usedStocks} fromZero={settings.fromZero} blade={blade} trim={trim} />
        )}
      </div>
    </Collapsible>
  );
}

// ─── Summary header ───────────────────────────────────────────────────────────

function SummaryHeader({ results }: { results: MaterialResult[] }) {
  const totalBars = results.reduce((s, r) => s + r.result.totalBars, 0);
  const totalPieces = results.reduce((s, r) => s + r.result.totalPieces, 0);

  // Weighted efficiency by number of bars
  const weightedEff = totalBars > 0
    ? Math.round(results.reduce((s, r) => s + r.result.efficiency * r.result.totalBars, 0) / totalBars)
    : 0;
  const effColor = weightedEff >= 90 ? 'var(--color-in-stock)' : weightedEff >= 75 ? '#d97706' : 'var(--color-red)';

  return (
    <div style={{
      background: 'var(--color-white)',
      border: BORDER,
      borderRadius: 8,
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 24,
      flexWrap: 'wrap',
    }}>
      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--color-black)', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 8 }}>
        Итого
      </span>
      <StatPill value={totalBars} label="заготовок" />
      <StatPill value={totalPieces} label="деталей размещено" />
      <StatPill value={`${weightedEff}%`} label="эффективность" color={effColor} />
    </div>
  );
}

function StatPill({ value, label, color = 'var(--color-black)' }: { value: string | number; label: string; color?: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      <span style={{ fontSize: 20, fontWeight: 700, color, fontFamily: 'Montserrat, sans-serif' }}>{value}</span>
      <span style={{ fontSize: 12, color: 'var(--color-gray)' }}>{label}</span>
    </span>
  );
}

// ─── CalcResultView (main export) ────────────────────────────────────────────

export function CalcResultView({ results, blade, trim, settings, onToggle }: Props) {
  if (results.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-400">
        Нет данных для отображения.
      </div>
    );
  }

  return (
    <div id="cutting-result-print" className="space-y-4">
      {/* Summary for 1D materials */}
      <SummaryHeader results={results} />

      {/* Report settings */}
      <div className="no-print">
      <Collapsible title="Настройка отчёта" defaultOpen={false}>
        <div style={{ padding: '0 16px 8px' }}>
          {REPORT_TOGGLES.map(({ key, label }) => (
            <Toggle
              key={key}
              label={label}
              checked={settings[key]}
              onChange={() => onToggle(key)}
            />
          ))}
        </div>
      </Collapsible>
      </div>

      {/* Per-material cards */}
      {results.map(({ material, result }) => (
        <MaterialResultCard
          key={material}
          material={material}
          result={result}
          settings={settings}
          blade={blade}
          trim={trim}
        />
      ))}
    </div>
  );
}
